import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Phone, Mail, Settings, ChevronDown, Calendar, Zap, Video, Lock } from 'lucide-react';
import { CompactLogo } from './AnimatedLogo';
import PromoCarousel from './PromoCarousel';
import { HeaderReviewButton } from './LeaveReviewButton';
import { useAuth } from '../context';
import { useSettings } from '../hooks/useSettings';

function phoneTelHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  return digits.startsWith('+') ? `tel:${digits}` : `tel:+${digits.replace(/^\+/, '')}`;
}

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user } = useAuth();
  const { settings, loading: settingsLoading } = useSettings();
  const isAuth = !!user;

  const phone = settings?.contact_phone?.trim();
  const email = settings?.contact_email?.trim();
  const address = settings?.contact_address?.trim();

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  // E-commerce category logic removed (Prod project)

  // Removed local auth effect as we use useAuth()

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gradient-fire text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 text-sm">
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap min-w-0">
              {settingsLoading ? (
                <>
                  <span className="hidden sm:inline w-28 h-4 bg-white/25 rounded animate-pulse" />
                  <span className="hidden sm:inline w-36 h-4 bg-white/25 rounded animate-pulse" />
                </>
              ) : (
                <>
                  {phone && (
                    <a href={phoneTelHref(phone)} className="flex items-center gap-2 hover:text-white/80 transition shrink-0">
                      <Phone className="w-4 h-4 shrink-0" />
                      <span className="hidden sm:inline">{phone}</span>
                    </a>
                  )}
                  {email && (
                    <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-white/80 transition shrink-0">
                      <Mail className="w-4 h-4 shrink-0" />
                      <span className="hidden md:inline">{email}</span>
                    </a>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end ml-auto">
              <HeaderReviewButton />
              {!settingsLoading && address && (
                <span className="text-xs sm:text-sm text-right leading-snug whitespace-normal">
                  {address}
                </span>
              )}
              {settingsLoading && (
                <span className="inline-block w-32 sm:w-48 h-4 bg-white/25 rounded animate-pulse" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <nav className="w-full px-4">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="transform hover:scale-105 transition-transform">
              <CompactLogo />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              <Link to="/" className="text-gray-700 hover:text-primary-500 font-medium transition-colors relative group">
                Accueil
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-fire group-hover:w-full transition-all duration-300"></span>
              </Link>

              {/* Enhanced Événementiel Dropdown */}
              <EventDropdown
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
              />

              <Link to="/portfolio" className="text-gray-700 hover:text-primary-500 font-medium transition-colors relative group">
                Galerie & Portfolio
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-fire group-hover:w-full transition-all duration-300"></span>
              </Link>

              <Link to="/about" className="text-gray-700 hover:text-primary-500 font-medium transition-colors relative group">
                À Propos
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-fire group-hover:w-full transition-all duration-300"></span>
              </Link>

              <Link to="/contact" className="text-gray-700 hover:text-primary-500 font-medium transition-colors relative group">
                Contact
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-fire group-hover:w-full transition-all duration-300"></span>
              </Link>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {isAuth && (
                <div className="flex items-center gap-3">
                  {user?.role === 'ADMIN' && (
                    <a
                      href={import.meta.env.VITE_ADMIN_URL || (import.meta.env.DEV ? 'http://localhost:5174' : '#')}
                      className="hidden lg:flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-all border border-gray-700"
                    >
                      <Settings size={18} className="animate-spin-slow" />
                      Administration
                    </a>
                  )}
                  <Link
                    to="/dashboard"
                    className="hidden md:inline-flex items-center px-4 py-2 bg-gradient-fire text-white rounded-lg font-medium text-sm hover:shadow-lg transition-all"
                  >
                    Tableau de bord
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 text-gray-700 hover:text-primary-500 transition-colors"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white animate-fade-in-down">
            <div className="px-4 py-4 space-y-3">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-500 rounded-lg transition-colors font-medium"
              >
                Accueil
              </Link>
              {/* Mobile Product links removed (Prod project) */}

              <div className="relative group">
                <Link
                  to="/mariage"
                  onClick={(e) => { e.preventDefault(); }}
                  className="flex items-center justify-between px-4 py-3 text-gray-400 cursor-not-allowed rounded-lg transition-colors font-medium border border-dashed border-gray-200"
                >
                  <span>Mariage Photo & Vidéo</span>
                  <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-600 rounded-full">
                    <Lock size={10} />
                    Bientôt
                  </span>
                </Link>
              </div>
              <div className="relative group">
                <Link
                  to="/photobooth"
                  onClick={(e) => { e.preventDefault(); }}
                  className="flex items-center justify-between px-4 py-3 text-gray-400 cursor-not-allowed rounded-lg transition-colors font-medium border border-dashed border-gray-200"
                >
                  <span>Photobooth</span>
                  <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-600 rounded-full">
                    <Lock size={10} />
                    Bientôt
                  </span>
                </Link>
              </div>
              <div className="relative group">
                <Link
                  to="/production"
                  onClick={(e) => { e.preventDefault(); }}
                  className="flex items-center justify-between px-4 py-3 text-gray-400 cursor-not-allowed rounded-lg transition-colors font-medium border border-dashed border-gray-200"
                >
                  <span>Production Vidéo</span>
                  <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-600 rounded-full">
                    <Lock size={10} />
                    Bientôt
                  </span>
                </Link>
              </div>
              <div className="relative group">
                <Link
                  to="/services"
                  onClick={(e) => { e.preventDefault(); }}
                  className="flex items-center justify-between px-4 py-3 text-gray-400 cursor-not-allowed rounded-lg transition-colors font-medium border border-dashed border-gray-200"
                >
                  <span>Tous les services</span>
                  <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-600 rounded-full">
                    <Lock size={10} />
                    Bientôt
                  </span>
                </Link>
              </div>
              <Link
                to="/portfolio"
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-500 rounded-lg transition-colors font-medium"
              >
                Galerie & Portfolio
              </Link>
              <Link
                to="/contact"
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-500 rounded-lg transition-colors font-medium"
              >
                Contact
              </Link>
              {isAuth && (
                <div className="space-y-2">
                  {user?.role === 'ADMIN' && (
                    <a
                      href={import.meta.env.VITE_ADMIN_URL || (import.meta.env.DEV ? 'http://localhost:5174' : '#')}
                      onClick={() => setIsMenuOpen(false)}
                      className="block px-4 py-3 bg-gray-900 text-white rounded-lg font-semibold text-center hover:bg-gray-800 transition-all"
                    >
                      Administration
                    </a>
                  )}
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-4 py-3 bg-gradient-fire text-white rounded-lg font-semibold text-center hover:shadow-lg transition-all"
                  >
                    Tableau de bord
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
      <PromoCarousel />
    </>
  );
}

function EventDropdown({ activeDropdown, setActiveDropdown }: any) {
  const events = [
    { title: 'Mariage Photo & Vidéo', desc: 'Reportage, film et album premium', icon: Calendar, link: '/mariage', locked: true },
    { title: 'Photobooth', desc: 'Location borne interactive mariage & corporate', icon: Zap, link: '/photobooth', locked: true },
    { title: 'Production Vidéo', desc: 'Corporate, clips et captation événement', icon: Video, link: '/production', locked: true },
    { title: 'Catalogue Services', desc: 'Toutes nos prestations photo-vidéo', icon: Video, link: '/services', locked: true },
  ];

  return (
    <div
      className="relative group py-8"
      onMouseEnter={() => setActiveDropdown('events')}
      onMouseLeave={() => setActiveDropdown(null)}
    >
      <button className="flex items-center gap-1.5 text-gray-700 hover:text-primary-500 font-medium transition-colors">
        <Video size={18} className="text-gray-400" />
        Réservations & Studio
        <ChevronDown size={14} className={`transition-transform duration-300 ${activeDropdown === 'events' ? 'rotate-180' : ''}`} />
      </button>

      <div className={`absolute top-full left-0 w-80 bg-white shadow-2xl rounded-2xl border border-gray-100 p-4 transition-all duration-300 origin-top transform ${activeDropdown === 'events' ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
        }`}>
        <div className="space-y-1">
          {events.map((ev, idx) => (
            <Link
              key={idx}
              to={ev.locked ? '#' : ev.link}
              onClick={(e) => ev.locked && e.preventDefault()}
              className={`flex items-center gap-4 p-3 rounded-xl transition-colors group/item text-left relative ${ev.locked ? 'opacity-60 cursor-not-allowed grayscale-[0.5]' : 'hover:bg-gray-50'}`}
            >
              <div className="w-12 h-12 bg-primary-50 text-primary-500 rounded-xl flex items-center justify-center group-hover/item:bg-primary-500 group-hover/item:text-white transition-colors">
                <ev.icon size={24} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-black text-gray-950 leading-tight mb-0.5">{ev.title}</p>
                <p className="text-[10px] text-gray-400 font-medium">{ev.desc}</p>
              </div>
              {ev.locked && (
                <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-600 rounded-full whitespace-nowrap">
                  <Lock size={10} />
                  Bientôt
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}


